/**
 * Verifier Flags Panel Component
 * 
 * Displays verification warnings and errors for a grading result.
 */

import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface VerificationFlag {
  type: string;
  severity: 'error' | 'warning';
  questionId: string;
  message: string;
  details?: Record<string, unknown>;
}

interface VerificationResult {
  id: string;
  grading_draft_id: string;
  valid: boolean;
  flags: string;
  error_count: number;
  warning_count: number;
  requires_review: boolean;
  review_priority: string;
}

interface VerifierFlagsPanelProps {
  verification?: VerificationResult;
}

const FLAG_TYPE_LABELS: Record<string, string> = {
  marks_exceed_max: '分數超過上限',
  negative_marks: '分數為負數',
  missing_evidence: '缺少證據',
  rubric_violation: '評分標準違規',
  confidence_low: '信心度低',
  transcript_quality: '轉錄品質問題',
  inconsistent_rationale: '理據不一致',
  missing_feedback: '缺少回饋',
};

const PRIORITY_LABELS: Record<string, { label: string; color: 'error' | 'warning' | 'info' | 'success' }> = {
  critical: { label: '緊急', color: 'error' },
  high: { label: '高', color: 'error' },
  medium: { label: '中', color: 'warning' },
  low: { label: '低', color: 'info' },
};

export function VerifierFlagsPanel({ verification }: VerifierFlagsPanelProps) {
  if (!verification) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          沒有找到驗證結果
        </Typography>
      </Paper>
    );
  }

  // Parse flags
  let flags: VerificationFlag[] = [];
  try {
    flags = JSON.parse(verification.flags);
  } catch {
    // Flags might be empty or malformed
  }

  const errors = flags.filter((f) => f.severity === 'error');
  const warnings = flags.filter((f) => f.severity === 'warning');

  const priorityInfo = PRIORITY_LABELS[verification.review_priority] || {
    label: verification.review_priority,
    color: 'info' as const,
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Summary */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {verification.valid ? (
            <CheckIcon color="success" sx={{ fontSize: 32 }} />
          ) : (
            <ErrorIcon color="error" sx={{ fontSize: 32 }} />
          )}
          <Typography variant="h6">
            {verification.valid ? '驗證通過' : '需要審核'}
          </Typography>
          {verification.requires_review && (
            <Chip
              label={`優先級: ${priorityInfo.label}`}
              color={priorityInfo.color}
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Chip
          icon={<ErrorIcon />}
          label={`${verification.error_count} 錯誤`}
          color={verification.error_count > 0 ? 'error' : 'default'}
        />
        <Chip
          icon={<WarningIcon />}
          label={`${verification.warning_count} 警告`}
          color={verification.warning_count > 0 ? 'warning' : 'default'}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Flags list */}
      {flags.length === 0 ? (
        <Alert severity="success" icon={<CheckIcon />}>
          沒有發現任何問題
        </Alert>
      ) : (
        <Stack spacing={2}>
          {/* Errors first */}
          {errors.map((flag, index) => (
            <Alert
              key={`error-${index}`}
              severity="error"
              icon={<ErrorIcon />}
            >
              <AlertTitle>
                {FLAG_TYPE_LABELS[flag.type] || flag.type}
              </AlertTitle>
              {flag.message}
              {flag.details && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(flag.details, null, 2)}
                  </Typography>
                </Box>
              )}
            </Alert>
          ))}

          {/* Then warnings */}
          {warnings.map((flag, index) => (
            <Alert
              key={`warning-${index}`}
              severity="warning"
              icon={<WarningIcon />}
            >
              <AlertTitle>
                {FLAG_TYPE_LABELS[flag.type] || flag.type}
              </AlertTitle>
              {flag.message}
              {flag.details && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(flag.details, null, 2)}
                  </Typography>
                </Box>
              )}
            </Alert>
          ))}
        </Stack>
      )}

      {/* Help text */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="body2">
            驗證系統會自動檢查評分結果是否符合規則。
            如有問題，請在「評分結果」頁籤中進行修改。
          </Typography>
        </Alert>
      </Box>
    </Paper>
  );
}

